/**
 * What the workspace says about an integration: its status in words, the
 * prompts that show what it is for, the credential form an api-key connector
 * needs, and the tagline under its name. Pure, so the list row and the
 * integration's own page read the same facts and the tests can pin them.
 *
 * @module components/apps/integrations/integrationContent
 */
import {
  DEFAULT_DOCUMENT_STORE_SCOPE,
  DOCUMENT_STORE_OPERATIONS,
  type DocumentStoreOperation,
} from "@amoreg/shared/integrationScope";

import type { Integration } from "../../../state/governance";

export { DEFAULT_DOCUMENT_STORE_SCOPE, DOCUMENT_STORE_OPERATIONS, type DocumentStoreOperation };

export interface IntegrationStatus {
  readonly key: "connected" | "pending" | "error" | "unconfigured" | "awaiting" | "disconnected";
  readonly label: string;
  /** Tailwind classes for the pill. */
  readonly className: string;
}

/**
 * The status pill. `partnerAccess` is whether this server can start a
 * partner-OAuth sign-in (Altruist): false says so honestly — "Awaiting
 * partner access" — instead of offering a Connect that cannot work; null
 * (unknown, or not a partner integration) changes nothing.
 */
export function integrationStatus(
  integration: Pick<Integration, "connected" | "connectionStatus" | "contract" | "configured">,
  partnerAccess: boolean | null = null,
): IntegrationStatus {
  if (integration.connected) {
    return { key: "connected", label: "Connected", className: "bg-success/10 text-success" };
  }
  if (integration.connectionStatus === "pending") {
    return { key: "pending", label: "Connecting", className: "bg-primary/10 text-primary" };
  }
  if (integration.connectionStatus === "error") {
    return {
      key: "error",
      label: "Needs attention",
      className: "bg-destructive/10 text-destructive",
    };
  }
  if (integration.contract === "api-key" && integration.configured !== true) {
    return {
      key: "unconfigured",
      label: "Not set up",
      className: "bg-muted text-muted-foreground",
    };
  }
  if (integration.contract === "partner-oauth" && partnerAccess === false) {
    return {
      key: "awaiting",
      label: "Awaiting partner access",
      className: "bg-muted text-muted-foreground",
    };
  }
  return {
    key: "disconnected",
    label: "Not connected",
    className: "bg-muted text-muted-foreground",
  };
}

/** The first thing someone should try, per connector; a sensible fallback otherwise. */
const PROMPTS: Readonly<Record<string, ReadonlyArray<string>>> = {
  plainsail: [
    "Search PlainSail for the latest compliance updates",
    "Summarise recent activity in PlainSail",
    "Find documents in PlainSail related to client onboarding",
  ],
  googledrive: [
    "Find the latest signed board minutes in Google Drive",
    "List policy documents in Google Drive changed this quarter",
    "Pull the AML training register from Google Drive",
  ],
  onedrive: [
    "Find the latest signed board minutes in OneDrive",
    "List policy documents in OneDrive changed this quarter",
    "Pull the AML training register from OneDrive",
  ],
  sharepoint: [
    "Find the compliance monitoring plan on SharePoint",
    "List documents in the Compliance site changed this month",
    "Pull the latest CDD exception log from SharePoint",
  ],
  laserfiche: [
    "Find the current AML policy in Laserfiche",
    "List what is in the Compliance folder in Laserfiche",
    "Read the latest board minutes filed in Laserfiche",
  ],
  ruleguard: [
    "What is open on the breaches register in RuleGuard?",
    "Which attestations are outstanding in RuleGuard this quarter?",
    "Summarise the gifts and hospitality register from RuleGuard",
  ],
  altruist: [
    "List the Altruist households we can see and how many accounts each holds",
    "Show the latest business-day positions for the Smith household's accounts in Altruist",
    "Summarise last month's Altruist transactions for one account and flag unusual activity",
  ],
  hubspot: [
    "Which HubSpot clients were onboarded this quarter?",
    "Show HubSpot contacts flagged as higher risk",
    "Summarise recent deal activity in HubSpot",
  ],
  salesforce: [
    "Which Salesforce accounts were onboarded this quarter?",
    "List Salesforce opportunities with a complaint or risk flag",
    "Summarise open Salesforce cases for our largest clients",
  ],
  jira: [
    "Which Jira compliance tickets are overdue?",
    "Summarise open Jira issues labelled audit or remediation",
    "List Jira tickets raised for policy exceptions this month",
  ],
};

export function integrationPrompts(
  integration: Pick<Integration, "id" | "name">,
): ReadonlyArray<string> {
  return (
    PROMPTS[integration.id] ?? [
      `Search ${integration.name} for the latest compliance updates`,
      `Summarise recent activity in ${integration.name}`,
      `Find documents in ${integration.name} related to client onboarding`,
    ]
  );
}

/** One line under the name; the description is the paragraph further down. */
export function integrationTagline(integration: Pick<Integration, "description">): string {
  const first = integration.description.split(/(?<=\.)\s/)[0] ?? integration.description;
  return first.length > 90 ? `${first.slice(0, 87).trimEnd()}…` : first;
}

export interface CredentialField {
  readonly name: string;
  readonly label: string;
  readonly placeholder: string;
  readonly secret: boolean;
  readonly hint?: string;
  /** A pasted file (a JSON key) rather than a line. Secret multiline fields still never echo back. */
  readonly multiline?: boolean;
  /** The input type for single-line, non-secret fields. Defaults to text. */
  readonly inputType?: "text" | "url";
  /** May be left empty; `credentialsComplete` decides what a connector needs. */
  readonly optional?: boolean;
  /**
   * Shown only when this way of connecting is chosen (`INTEGRATION_CREDENTIAL_MODES`).
   * Absent: shown in every mode.
   */
  readonly mode?: string;
  /** Where the firm's IT admin finds this value — a hover card beside the label. */
  readonly guide?: FieldGuide;
}

/** The short, IT-facing note behind a field's info mark. */
export interface FieldGuide {
  /** One sentence: what this is. */
  readonly what: string;
  /** Where to get it, as steps an admin follows. */
  readonly steps: ReadonlyArray<string>;
}

/** One way of connecting an integration that has more than one. */
export interface CredentialMode {
  readonly id: string;
  readonly label: string;
  readonly description: string;
}

/**
 * Integrations with two ways in show a switch and only that way's fields.
 * The stored values carry only the chosen way's fields, so a half-typed
 * other way never rides along.
 */
export const INTEGRATION_CREDENTIAL_MODES: Readonly<Record<string, ReadonlyArray<CredentialMode>>> =
  {
    laserfiche: [
      {
        id: "cloud",
        label: "Laserfiche Cloud",
        description: "app.laserfiche.com and the regional clouds",
      },
      {
        id: "self-hosted",
        label: "Self-hosted",
        description: "Laserfiche API Server on your own servers",
      },
    ],
  };

/** The way an integration's form starts in: its first mode, or none. */
export const defaultCredentialMode = (connectorId: string): string | null =>
  INTEGRATION_CREDENTIAL_MODES[connectorId]?.[0]?.id ?? null;

/**
 * Where a field's typed value lives in the form's value map. A field that
 * belongs to a mode is keyed under that mode, so two modes sharing a name
 * (both Laserfiche ways ask for a `repositoryId`, meaning different things)
 * never read each other's value.
 */
export const credentialValueKey = (field: Pick<CredentialField, "name" | "mode">): string =>
  field.mode === undefined ? field.name : `${field.mode}:${field.name}`;

/** The fields shown for a connector in a mode: mode-less ones, plus that mode's. */
export function credentialFieldsFor(
  connectorId: string,
  mode: string | null,
): ReadonlyArray<CredentialField> {
  return (INTEGRATION_FIELD_SPECS[connectorId] ?? []).filter(
    (field) => field.mode === undefined || field.mode === mode,
  );
}

/**
 * The credential form each api-key connector needs, by connector id. The API
 * stores whatever values it is given, so adding a connector's contract is one
 * entry here.
 */
export const INTEGRATION_FIELD_SPECS: Readonly<Record<string, ReadonlyArray<CredentialField>>> = {
  imanage: [
    {
      name: "baseUrl",
      label: "iManage Cloud URL",
      placeholder: "https://yourfirm.cloudimanage.com",
      secret: false,
      inputType: "url",
    },
    {
      name: "customerId",
      label: "Customer ID",
      placeholder: "Your iManage customer ID",
      secret: false,
    },
    {
      name: "clientId",
      label: "OAuth client ID",
      placeholder: "Registered Amoreg application ID",
      secret: false,
    },
    {
      name: "clientSecret",
      label: "OAuth client secret",
      placeholder: "Stored encrypted; never shown again",
      secret: true,
    },
  ],
  plainsail: [
    {
      name: "baseUrl",
      label: "API base URL",
      placeholder: "https://yourfirm.plainsail.je",
      secret: false,
      inputType: "url",
      hint: "The origin PlainSail gave your firm for API access.",
    },
    {
      name: "apiKey",
      label: "API key",
      placeholder: "ps_live_…",
      secret: true,
      hint: "Stored encrypted. Amoreg reads PlainSail with it; it is never shown again.",
    },
  ],
  sharepoint: [
    {
      name: "tenantId",
      label: "Directory (tenant) ID",
      placeholder: "00000000-0000-0000-0000-000000000000",
      secret: false,
      hint: "From the app registration's Overview page in Microsoft Entra.",
      guide: {
        what: "Your Microsoft 365 tenant.",
        steps: [
          "Microsoft Entra admin center → App registrations → the Amoreg app → Overview → Directory (tenant) ID.",
        ],
      },
    },
    {
      name: "clientId",
      label: "Application (client) ID",
      placeholder: "00000000-0000-0000-0000-000000000000",
      secret: false,
      hint: "Also on the Overview page.",
      guide: {
        what: "The app registration Amoreg signs in as.",
        steps: [
          "Register an app in Microsoft Entra (any name; Amoreg is fine).",
          "API permissions → add Microsoft Graph application permission Sites.Selected → grant admin consent.",
          "Overview → Application (client) ID.",
        ],
      },
    },
    {
      name: "clientSecret",
      label: "Client secret",
      placeholder: "The secret's value, not its ID",
      secret: true,
      hint: "Stored encrypted; never shown again. Set a reminder for its expiry date.",
      guide: {
        what: "The app's password.",
        steps: [
          "Certificates & secrets → New client secret → copy the Value column (not the Secret ID).",
          "Then grant the app each site: Grant-PnPAzureADAppSitePermission, or POST sites/{id}/permissions in Graph.",
        ],
      },
    },
  ],
  googledrive: [
    {
      name: "serviceAccountJson",
      label: "Service account key (JSON)",
      placeholder: '{ "type": "service_account", "client_email": "…", "private_key": "…" }',
      secret: true,
      multiline: true,
      hint: "Paste the whole key file. Stored encrypted; never shown again.",
      guide: {
        what: "A robot identity that sees only the folders shared with its email address.",
        steps: [
          "Google Cloud console → IAM & Admin → Service Accounts → create one (any project).",
          "Keys → Add key → JSON. Download it and paste the whole file here.",
          "APIs & Services → enable the Google Drive API for that project.",
          "In Drive, share each folder Amoreg should see with the account's email (Viewer).",
        ],
      },
    },
  ],
  laserfiche: [
    {
      name: "accessKey",
      label: "Service app access key",
      placeholder:
        '{ "customerId": "…", "clientId": "…", "domain": "laserfiche.com", "jwk": { … } }',
      secret: true,
      multiline: true,
      mode: "cloud",
      hint: "The JSON file (or its base64 form) the Developer Console exported. Stored encrypted; never shown again.",
      guide: {
        what: "Identifies the Service App and signs its sign-in; it holds no secret of its own.",
        steps: [
          "Laserfiche Developer Console → your Service App (create one if needed: type Service App, bound to the Amoreg service principal).",
          "Allow the repository.Read scope on the app.",
          "Under the app's Authentication settings, download the access key. Paste the whole file here.",
        ],
      },
    },
    {
      name: "servicePrincipalKey",
      label: "Service principal key",
      placeholder: "The key Account Administration generated",
      secret: true,
      mode: "cloud",
      hint: "Stored encrypted; never shown again. Keys expire (90 days by default) — set a reminder.",
      guide: {
        what: "The secret of the non-person account Amoreg signs in as.",
        steps: [
          "Laserfiche Account Administration → Service Principals → create one named Amoreg.",
          "Give it read access to the folders Amoreg should see, and nothing else.",
          "Generate a service principal key; it is shown once. Copy it here.",
        ],
      },
    },
    {
      name: "repositoryId",
      label: "Repository",
      placeholder: "r-abc123",
      secret: false,
      optional: true,
      mode: "cloud",
      hint: "Only needed when the account has more than one repository.",
      guide: {
        what: "Which repository Amoreg works in.",
        steps: [
          "Leave empty if the account has one repository — it is found automatically.",
          "Otherwise the repository id (r-…) from the web client's address bar (repo=…), or its name.",
        ],
      },
    },
    {
      name: "serverUrl",
      label: "API Server URL",
      placeholder: "https://laserfiche.yourfirm.com",
      secret: false,
      inputType: "url",
      mode: "self-hosted",
      hint: "The address of your Laserfiche API Server; /LFRepositoryAPI is added for you.",
      guide: {
        what: "Where the Laserfiche API Server answers (Laserfiche 11 or later).",
        steps: [
          "The https origin of the API Server, e.g. https://laserfiche.yourfirm.com.",
          "It must be reachable from the internet, or from wherever Amoreg's server runs. https only.",
        ],
      },
    },
    {
      name: "username",
      label: "Repository user",
      placeholder: "amoreg",
      secret: false,
      mode: "self-hosted",
      hint: "A repository or directory user created for Amoreg.",
      guide: {
        what: "The account Amoreg signs in as; its rights are Amoreg's rights.",
        steps: [
          "Laserfiche Administration → Users → create a repository user named Amoreg (a directory user works too, as user@domain).",
          "Grant it read rights on the folders Amoreg should see, and nothing else.",
        ],
      },
    },
    {
      name: "password",
      label: "Password",
      placeholder: "",
      secret: true,
      mode: "self-hosted",
      hint: "Stored encrypted; never shown again.",
      guide: {
        what: "That user's password.",
        steps: ["Set a long password; Amoreg signs in with it and never shows it again."],
      },
    },
    {
      name: "repositoryId",
      label: "Repository name",
      placeholder: "FirmRepo",
      secret: false,
      mode: "self-hosted",
      hint: "As shown in the Laserfiche web client.",
      guide: {
        what: "The repository this server hosts that Amoreg works in.",
        steps: [
          "The repository's name as the web client shows it — the same one the user signs in to.",
        ],
      },
    },
  ],
  ruleguard: [
    {
      name: "subdomain",
      label: "RuleGuard subdomain",
      placeholder: "yourfirm",
      secret: false,
      hint: "The first part of the address you sign in at: yourfirm.ruleguard.com.",
      guide: {
        what: "Which RuleGuard tenant is yours.",
        steps: [
          "Open RuleGuard and read the address bar: https://yourfirm.ruleguard.com.",
          "Enter yourfirm — pasting the whole address is fine.",
        ],
      },
    },
    {
      name: "username",
      label: "Service account username",
      placeholder: "amoreg@yourfirm.com",
      secret: false,
      hint: "A RuleGuard user created for Amoreg. What it may see in RuleGuard is what Amoreg may.",
      guide: {
        what: "The RuleGuard user Amoreg signs in as; RuleGuard applies its permissions to every call.",
        steps: [
          "RuleGuard → Administration → Users → add a user for Amoreg (a mailbox you control).",
          "Give it a role with read access to the registers and modules Amoreg should see.",
          "It must sign in with a password, not through single sign-on.",
        ],
      },
    },
    {
      name: "password",
      label: "Service account password",
      placeholder: "",
      secret: true,
      hint: "Stored encrypted; never shown again.",
      guide: {
        what: "That user's password.",
        steps: ["Set a long password for the account; Amoreg signs in with it on your behalf."],
      },
    },
  ],
};

/**
 * Whether a form's values are enough to connect: every required field of
 * the chosen way in (Laserfiche has two; the rest have one).
 */
export function credentialsComplete(
  connectorId: string,
  values: Readonly<Record<string, string>>,
  mode: string | null = defaultCredentialMode(connectorId),
): boolean {
  const fields = credentialFieldsFor(connectorId, mode);
  if (fields.length === 0) return false;
  const has = (key: string): boolean => (values[key] ?? "").trim().length > 0;
  return fields.every((field) => field.optional === true || has(credentialValueKey(field)));
}

/**
 * The values to store: only the fields the chosen mode shows, so switching
 * from Cloud to self-hosted never sends a half-typed access key along.
 */
export function credentialValuesForMode(
  connectorId: string,
  values: Readonly<Record<string, string>>,
  mode: string | null,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of credentialFieldsFor(connectorId, mode)) {
    const value = values[credentialValueKey(field)];
    if (value !== undefined) out[field.name] = value;
  }
  return out;
}

/**
 * What the firm's admin does on their side before the form can be filled —
 * the part of connecting that happens in Entra or Google, not here. Written
 * for the person who will actually do it.
 */
export const INTEGRATION_SETUP_GUIDES: Readonly<Record<string, ReadonlyArray<string>>> = {
  sharepoint: [
    'In the Microsoft Entra admin center, register an application (any name — "Amoreg" is fine) and note its Application (client) ID and Directory (tenant) ID from the Overview page.',
    "Under API permissions, add the Microsoft Graph application permission Sites.Selected and grant admin consent. On its own it reaches nothing: Selected permissions need an explicit grant per site.",
    'Grant the app each site Amoreg should see, with the read role: POST https://graph.microsoft.com/v1.0/sites/{siteId}/permissions with { roles: ["read"], grantedToIdentities: [{ application: { id, displayName } }] } (Graph Explorer, or PnP PowerShell: Grant-PnPAzureADAppSitePermission -Permissions Read). Granting needs Sites.FullControl.All or a site owner. Use write only if Amoreg should upload copies.',
    "Under Certificates & secrets, create a client secret and copy its Value (not the Secret ID). Enter the three values; saving connects.",
    'Optionally narrow reach further under "What Amoreg can reach": the same sites, and folders inside them.',
  ],
  googledrive: [
    "In the Google Cloud console, create a service account (IAM & Admin → Service Accounts, any project) and add a JSON key to it (Keys → Add key → JSON). Download the file.",
    "Enable the Google Drive API for that project (APIs & Services → Library).",
    "In Drive, share each folder Amoreg should see with the service account's email address as Viewer (Editor only if Amoreg should upload copies). For a shared drive, add the account as a member instead. Nothing you do not share is visible to it.",
    'Paste the key file; saving connects. Optionally narrow reach further under "What Amoreg can reach".',
  ],
  laserfiche: [
    'Laserfiche Cloud: in Account Administration → Users → Service Principals, add a service principal (a non-person account — "Amoreg" is fine), give it access to the Laserfiche services, and set its entry access rights to the folders Amoreg should see (Repository Administration). Generate a service principal key — it is shown once, and expires after 90 days unless changed under Settings → Integration Configuration.',
    "In the Developer Console, create a Service App bound to that principal, allow the repository.Read scope, and download its access key (the JSON file). Paste the key and the principal key.",
    "Self-hosted (Laserfiche API Server 11 or later): instead, create a repository user with read rights on the right folders, and enter the API Server address, that user, its password and the repository name.",
    'Saving checks the credential with Laserfiche. Optionally narrow reach further under "What Amoreg can reach" — folder paths like \\Compliance\\Policies, or entry ids.',
  ],
  ruleguard: [
    'In RuleGuard, create a user for Amoreg (a service account — "Amoreg" is fine) with the roles it should have. RuleGuard applies that user\'s permissions to everything Amoreg does through its MCP server, as it does for any user. The account must sign in with a password, not single sign-on.',
    "Enter your firm's RuleGuard subdomain and that user's sign-in details. Saving signs in once to check them.",
    'Under "What Amoreg can reach", the tools RuleGuard offers this account appear as a list; tick the ones Amoreg may call, or leave all. Looking things up is on by default; switch on "Change records" only if Amoreg should create or amend entries.',
  ],
};

/** A numbered mark placed over a screen, as percentages of its width and height. */
export interface ScreenCallout {
  readonly x: number;
  readonly y: number;
  /** The field on our form this part of the vendor's screen feeds. */
  readonly field: string;
  readonly detail: string;
}

/**
 * A picture of the vendor's own app for the IT guide's dialog: what the
 * admin will be looking at, with marks tying it to the fields on our form.
 * Only public, unauthenticated screens are captured (see
 * `apps/server/scripts/capture-integration-screens.mjs`); anything behind
 * a sign-in is described in words instead.
 */
export interface SetupScreen {
  readonly src: string;
  readonly alt: string;
  readonly title: string;
  /** Intrinsic size, so the frame holds its shape before the picture arrives. */
  readonly width: number;
  readonly height: number;
  /**
   * The address the screen lives at, shown above it as a browser bar of
   * ours with the tenant part picked out — for a vendor where the address
   * itself is a value the form asks for.
   */
  readonly address?: {
    readonly tenant: string;
    readonly rest: string;
    readonly field: string;
    readonly detail: string;
  };
  /** Marks over the picture itself; the address mark, if any, comes first. */
  readonly callouts: ReadonlyArray<ScreenCallout>;
  /** Where and when it was taken; vendors redesign, and the guide should say so. */
  readonly source: { readonly label: string; readonly href: string; readonly captured: string };
}

export const INTEGRATION_SETUP_SCREENS: Readonly<Record<string, ReadonlyArray<SetupScreen>>> = {
  ruleguard: [
    {
      src: "/integrations/ruleguard/sign-in.webp",
      alt: "RuleGuard's sign-in page: the RuleGuard logo, a Login heading, Email and Password fields and a Login button on a white card.",
      title: "The sign-in page of your firm's RuleGuard",
      width: 1408,
      height: 1580,
      address: {
        tenant: "yourfirm",
        rest: ".ruleguard.com",
        field: "RuleGuard subdomain",
        detail:
          "The first part of the address your team opens RuleGuard at — everything before .ruleguard.com. For demo.ruleguard.com it is demo.",
      },
      callouts: [
        {
          x: 84,
          y: 38.3,
          field: "Service account username",
          detail:
            "The email of the user created for Amoreg. It signs in here with a password, so it must not be a single-sign-on user.",
        },
        {
          x: 84,
          y: 50.3,
          field: "Service account password",
          detail: "That user's password. Amoreg signs in with it and never shows it again.",
        },
      ],
      source: {
        label: "demo.ruleguard.com",
        href: "https://demo.ruleguard.com/login",
        captured: "2026-09-15",
      },
    },
  ],
};

/** Where the vendor documents the setup, for the IT guide's footer. */
export const INTEGRATION_SETUP_LINKS: Readonly<
  Record<string, ReadonlyArray<{ readonly label: string; readonly href: string }>>
> = {
  sharepoint: [
    {
      label: "Sites.Selected permission",
      href: "https://learn.microsoft.com/en-us/graph/permissions-selected-overview",
    },
  ],
  googledrive: [
    {
      label: "Service accounts",
      href: "https://cloud.google.com/iam/docs/service-accounts-create",
    },
  ],
  laserfiche: [
    {
      label: "Service principals",
      href: "https://developer.laserfiche.com/docs/api/authentication/guide_service-principals/",
    },
    {
      label: "Service apps",
      href: "https://developer.laserfiche.com/docs/api/authentication/guide_oauth-service/",
    },
    {
      label: "Self-hosted API Server",
      href: "https://developer.laserfiche.com/docs/api/server/",
    },
  ],
  ruleguard: [{ label: "RuleGuard", href: "https://www.ruleguard.com/" }],
};

export interface ScopeSpec {
  /**
   * What decides reach on the vendor's side, in the vendor's own terms.
   * The boundary Amoreg enforces sits inside this one; the sentence tells
   * an admin where the outer boundary is set so the two are not confused.
   */
  readonly providerControl: string;
  /**
   * How the organization narrows reach: named places (document stores) or
   * named tools picked from the vendor's live catalogue (RuleGuard).
   */
  readonly kind: "locations" | "tools";
  readonly locationLabel: string;
  readonly locationPlaceholder: string;
  readonly locationHint: string;
  /** What "nothing listed" means here. */
  readonly unlimitedHint: string;
  /** The operations that mean something for this integration, in display order. */
  readonly operations: ReadonlyArray<DocumentStoreOperation>;
  /** Wording for an operation where the document-store phrasing does not fit. */
  readonly operationLabels?: Partial<
    Record<DocumentStoreOperation, { readonly label: string; readonly description: string }>
  >;
}

/** What a document store can be asked to do. */
const DOCUMENT_STORE_OPERATION_SET: ReadonlyArray<DocumentStoreOperation> = [
  "search",
  "list",
  "read",
  "download",
  "upload",
];

/**
 * The scope editor per integration. Each `providerControl` states the
 * vendor's own mechanism as its documentation describes it:
 *
 * - SharePoint: Microsoft Graph Selected permissions — an app consented for
 *   `Sites.Selected` has no access until an admin grants it a site (or, with
 *   the newer `*.SelectedOperations.Selected` scopes, a list, folder or
 *   file) with a role of read, write, owner or fullcontrol.
 * - Google Drive: a service account is an identity like any other; it sees
 *   only what is shared with its email address, or shared drives it is a
 *   member of, with the role given.
 * - Laserfiche: a service principal (or repository user) is a trustee like
 *   any other; entry access rights on folders decide what it reaches.
 * - RuleGuard: "authenticated, permissioned access governed by the same
 *   audit and permission controls as the rest of the platform — and you
 *   control exactly what is shared" (ruleguard.com/faq); the service
 *   account's roles decide.
 */
export const INTEGRATION_SCOPE_SPECS: Readonly<Record<string, ScopeSpec>> = {
  imanage: {
    providerControl: "Each user can access only what their own iManage account permits.",
    kind: "locations",
    unlimitedHint: "Nothing listed: all libraries this user can access.",
    operations: DOCUMENT_STORE_OPERATION_SET,
    locationLabel: "Libraries",
    locationPlaceholder: "ACTIVE",
    locationHint:
      "Optional iManage library IDs. An empty list permits the libraries each signed-in user can access.",
  },
  sharepoint: {
    providerControl:
      "Microsoft 365 sets the outer boundary: the app registration reaches only the sites an admin granted it (Sites.Selected), with the role granted — read, or write for uploads.",
    kind: "locations",
    locationLabel: "Sites and folders",
    locationPlaceholder:
      "https://yourfirm.sharepoint.com/sites/Compliance/Shared Documents/Policies",
    locationHint:
      "A site URL, optionally with a folder inside its document library — paste it from the address bar. Files count when the folder is anywhere above them.",
    unlimitedHint: "Nothing listed: every site the app registration was granted.",
    operations: DOCUMENT_STORE_OPERATION_SET,
  },
  googledrive: {
    providerControl:
      "Google Drive sets the outer boundary: the service account sees only folders shared with its email address (or shared drives it is a member of), with the role given — Viewer, or Editor for uploads.",
    kind: "locations",
    locationLabel: "Folders",
    locationPlaceholder: "https://drive.google.com/drive/folders/…",
    locationHint:
      "A folder URL or id — paste it from the address bar. Files count when the folder is anywhere above them.",
    unlimitedHint: "Nothing listed: every folder shared with the service account.",
    operations: DOCUMENT_STORE_OPERATION_SET,
  },
  laserfiche: {
    providerControl:
      "Laserfiche sets the outer boundary: the service principal (or repository user) reaches only the folders its entry access rights allow.",
    kind: "locations",
    locationLabel: "Folders",
    locationPlaceholder: "\\Compliance\\Policies",
    locationHint:
      "A repository folder path, or a folder's entry id. Entries count when the folder is anywhere above them.",
    unlimitedHint: "Nothing listed: every folder the account's access rights allow.",
    // Read-only: nothing in the workspace writes to a Laserfiche repository.
    operations: ["search", "list", "read", "download"],
  },
  ruleguard: {
    providerControl:
      "RuleGuard sets the outer boundary: the service account's roles decide what it can see and do, and RuleGuard applies the same permission controls to this connection as to the rest of the platform.",
    kind: "tools",
    locationLabel: "Tools Amoreg may call",
    locationPlaceholder: "",
    locationHint:
      "RuleGuard describes its own tools; this is the catalogue the service account can use, read live. Tick the ones Amoreg may call.",
    unlimitedHint: "Nothing ticked: every tool the service account can use.",
    operations: ["read", "write"],
    operationLabels: {
      read: {
        label: "Look things up",
        description:
          "Tools RuleGuard marks read-only — registers, policies, attestations, monitoring. Shown as “read-only” in the list.",
      },
      write: {
        label: "Change records",
        description:
          "Every other tool — ones that create or amend entries, and any RuleGuard has not marked read-only. Off, Amoreg only looks things up.",
      },
    },
  },
};

export const DOCUMENT_STORE_OPERATION_LABELS: Readonly<
  Record<DocumentStoreOperation, { readonly label: string; readonly description: string }>
> = {
  search: { label: "Search", description: "Find files by keyword." },
  list: { label: "List", description: "See what is in a folder." },
  read: { label: "Read", description: "Open a file's text and details." },
  download: {
    label: "Download",
    description: "Take whole files out of the store, e.g. to read a PDF or Word document.",
  },
  upload: {
    label: "Upload",
    description:
      "Place copies of workspace files in the store, so a spreadsheet can be opened there. The credential itself must have write access.",
  },
  write: {
    label: "Change records",
    description:
      "Call every other tool — ones that create or amend entries, and any RuleGuard has not declared read-only. Off, Amoreg calls only declared reads. The service account itself must hold the right.",
  },
};

/** Whether the integration has a scope editor (a firm-scoped document store). */
export const hasScopeEditor = (integration: Pick<Integration, "id">): boolean =>
  integration.id in INTEGRATION_SCOPE_SPECS;

/** The one line under "Connects with" — who issues the credential. */
export function integrationConnectsWith(integration: Pick<Integration, "id" | "contract">): string {
  switch (integration.id) {
    case "sharepoint":
      return "An app registration your Microsoft 365 admin created";
    case "googledrive":
      return "A service account your Google Workspace admin created";
    case "laserfiche":
      return "A service app (Cloud) or repository user (self-hosted) your Laserfiche admin created";
    case "ruleguard":
      return "A service account your RuleGuard admin created";
    case "altruist":
      return "An Altruist advisor or admin login, read-only";
    default:
      return integration.contract === "api-key"
        ? "Your firm's API credentials"
        : "OAuth via the Amoreg platform";
  }
}

/** Where the integration is brokered, for the Information list. */
export function integrationDeveloper(integration: Pick<Integration, "source">): string {
  return integration.source === "composio" ? "Composio" : "Amoreg";
}

/**
 * Connected integrations first, then the rest alphabetically — the list is a
 * status board before it is a catalogue.
 */
export function sortIntegrations<T extends Pick<Integration, "name" | "connected">>(
  integrations: ReadonlyArray<T>,
): ReadonlyArray<T> {
  return [...integrations].sort((left, right) => {
    const connected = Number(right.connected) - Number(left.connected);
    return connected !== 0 ? connected : left.name.localeCompare(right.name);
  });
}

/**
 * The one action a list row offers. Switching an integration on is only
 * meaningful once something can answer: stored credentials for an api-key
 * integration, a signed-in grant for a partner-OAuth one; otherwise the row
 * links to the integration's page to set it up. The switch follows the
 * org's own `enabled` flag, so a connected integration that is switched off
 * offers "Turn on" — never a "Turn off" that would switch it on.
 */
export function integrationRowAction(
  integration: Pick<Integration, "contract" | "configured" | "connected" | "enabled"> &
    Partial<Pick<Integration, "id">>,
): "turn-off" | "turn-on" | "set-up" | "view-details" {
  if (integration.id === "imanage") return "view-details";
  const usable =
    integration.contract === "api-key"
      ? integration.configured === true
      : integration.contract === "partner-oauth"
        ? integration.connected
        : true;
  if (integration.enabled && (integration.connected || usable)) return "turn-off";
  return usable ? "turn-on" : "set-up";
}
